import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { buildLearningEbookReaderPath } from '@app/routes/route-paths'
import { ArrowLeft, BookOpen, Languages, LoaderCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
  LmsButton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/ui'
import type {
  LearningContentItem,
  LearningWorkbookChapter,
} from '@services/firebase/learning'
import {
  addDailyActivityEntry,
  getElapsedDailyActivitySeconds,
  getSelfStudyContent,
  saveSelfStudyStepProgress,
} from '@services/firebase/self-study'
import { updateDashboardProfile } from '@services/firebase'
import {
  SelfStudyStepRenderer,
  type SelfStudyContinueResult,
  type SelfStudySlide,
} from './SelfStudyStepRenderer'
import {
  LearningLessonShell,
  LearningLessonShellContent,
} from '@features/learning/shared/LearningLessonShell'
import { LearningExerciseToolbar } from '@features/learning/shared/learning-exercise-toolbar'
import {
  getDashboardLocaleKey,
  normalizeDashboardLanguageValue,
  useDashboardState,
} from '@features/dashboard'
import { useTranslation } from 'react-i18next'
import './self-study.css'
import './self-study-media.css'
import './self-study-exercises.css'
import './templates/conversation-template.css'
import './templates/vocabulary-template.css'
import './self-study-responsive.css'
import '@shared/styles/legacy-learning-responsive-shell.css'
import './self-study-native.css'

export function SelfStudyChapterDialog({
  chapter,
  onClose,
  onProgressPersisted,
  open,
}: {
  readonly chapter: LearningWorkbookChapter | null
  readonly onClose: () => void
  readonly onProgressPersisted?: (personalization: Record<string, unknown>) => void | Promise<void>
  readonly open: boolean
}) {
  const navigate = useNavigate()
  const { actions, state } = useDashboardState()
  const { i18n, t } = useTranslation()
  const [content, setContent] = useState<LearningContentItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isPersisting, setIsPersisting] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [step, setStep] = useState(-1)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [hasRecordedCompletion, setHasRecordedCompletion] = useState(false)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false)
  const [isSavingLanguage, setIsSavingLanguage] = useState(false)
  const [languageErrorMessage, setLanguageErrorMessage] = useState<string | null>(null)
  const nextActionInFlightRef = useRef(false)
  const progressPercent =
    content.length > 0
      ? Math.min(Math.max(((step + 1) / content.length) * 100, 0), 100)
      : 0
  const backToLearningLabel = t('Back to learning', { ns: 'book' })
  const changeLanguageLabel = t('Change language', { ns: 'book' })
  const openLessonBookLabel = t('Open lesson book', { ns: 'book' })

  useEffect(() => {
    if (!open || !chapter?.id) {
      return
    }

    let isMounted = true

    const loadContent = async () => {
      try {
        setIsLoading(true)
        setErrorMessage(null)
        setStep(-1)
        setStartedAt(Date.now())
        setElapsedSeconds(0)
        setHasRecordedCompletion(false)
        setInfoMessage(null)
        setIsTransitioning(false)
        nextActionInFlightRef.current = false
        const nextContent = await getSelfStudyContent(chapter.id)

        if (!isMounted) {
          return
        }

        setContent(nextContent)
      } catch (error) {
        if (!isMounted) {
          return
        }

        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to load the self-study chapter content.',
        )
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    void loadContent()

    return () => {
      isMounted = false
    }
  }, [chapter?.id, open])

  const currentSlide = useMemo<SelfStudySlide | null>(() => {
    if (!chapter) {
      return null
    }

    if (step < 0) {
      return { kind: 'introduction' }
    }

    if (step >= content.length) {
      return { kind: 'implementation' }
    }

    return {
      item: content[step],
      kind: 'content',
    }
  }, [chapter, content, step])

  useEffect(() => {
    if (!open || !chapter || currentSlide?.kind !== 'implementation' || hasRecordedCompletion) {
      return
    }

    let isMounted = true

    const recordDailyActivity = async () => {
      try {
        const seconds = getElapsedDailyActivitySeconds({ startedAt })
        setElapsedSeconds(seconds)
        const activity = await addDailyActivityEntry({
          seconds,
        })

        if (!isMounted) {
          return
        }

        actions.mergeMyUser({
          daily_activities: (() => {
            const existingActivities = state.myUser?.daily_activities ?? []
            const existingIndex = existingActivities.findIndex(
              (entry) => entry.id === activity.id,
            )

            if (existingIndex >= 0) {
              const nextActivities = [...existingActivities]
              nextActivities[existingIndex] = activity
              return nextActivities
            }

            return [...existingActivities, activity]
          })(),
        })
        setHasRecordedCompletion(true)
      } catch {
        if (isMounted) {
          setInfoMessage(
            'Study time could not be recorded. Your chapter completion remains available, but the Daily goal total may need retrying.',
          )
          setHasRecordedCompletion(true)
        }
      }
    }

    void recordDailyActivity()

    return () => {
      isMounted = false
    }
  }, [
    actions,
    chapter,
    currentSlide?.kind,
    hasRecordedCompletion,
    open,
    startedAt,
    state.myUser?.daily_activities,
  ])

  const handleNext = async () => {
    if (
      !chapter ||
      !currentSlide ||
      isPersisting ||
      isTransitioning ||
      nextActionInFlightRef.current
    ) {
      return
    }

    nextActionInFlightRef.current = true
    const selfStudyId =
      currentSlide.kind === 'introduction'
        ? 'introduction'
        : currentSlide.kind === 'implementation'
          ? 'implementation'
          : currentSlide.item.id

    try {
      setIsPersisting(true)
      if (!state.book?.id) {
        throw new Error('Learning program is unavailable.')
      }
      const personalization = await saveSelfStudyStepProgress({
        bookId: state.book.id,
        chapterId: chapter.id,
        selfStudyId,
      })
      actions.mergeMyUser({
        personalization,
      })
      if (currentSlide.kind === 'implementation') {
        void onProgressPersisted?.(personalization)
        onClose()
        return
      }
      setIsTransitioning(true)
      window.setTimeout(() => {
        setStep((previous) => previous + 1)
        setIsTransitioning(false)
      }, 120)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to save self-study progress.',
      )
    } finally {
      nextActionInFlightRef.current = false
      setIsPersisting(false)
    }
  }

  const handleContinue = (result: SelfStudyContinueResult) => {
    if (continuePending) {
      return
    }

    if (nextActionInFlightRef.current) {
      return
    }

    if (currentSlide?.kind === 'content' && result === 'incorrect') {
      setContent((previous) => [...previous, currentSlide.item])
    }

    void handleNext()
  }

  const handleOpenOnlineBook = () => {
    if (!chapter?.lessonId) {
      setInfoMessage('This chapter is not linked to an online lesson yet.')
      return
    }

    const lesson = state.lessons.find((entry) => entry.id === chapter.lessonId)

    if (lesson) {
      actions.setLesson(lesson)
      onClose()
      navigate(
        buildLearningEbookReaderPath(state.book?.id, {
          preserveCurrentSearch: true,
        }),
      )
      return
    }

    setInfoMessage(
      'The linked online lesson is not available in the current lesson catalog.',
    )
  }

  const handleRequestClose = () => {
    if (window.confirm('Do you want to exit?')) {
      onClose()
    }
  }

  const handleOpenLanguage = () => {
    setLanguageErrorMessage(null)
    setIsLanguageDialogOpen(true)
  }

  const handleCloseLanguageDialog = () => {
    if (!isSavingLanguage) {
      setIsLanguageDialogOpen(false)
    }
  }

  const handleSaveLanguage = async (value: string) => {
    const normalizedValue = normalizeDashboardLanguageValue(value)

    try {
      setIsSavingLanguage(true)
      setLanguageErrorMessage(null)
      await updateDashboardProfile({ language_default: normalizedValue })
      actions.mergeMyUser({ language_default: normalizedValue })
      await i18n.changeLanguage(normalizedValue)
      setIsLanguageDialogOpen(false)
    } catch {
      setLanguageErrorMessage(t('Updated failed'))
    } finally {
      setIsSavingLanguage(false)
    }
  }

  const continuePending = isPersisting || isTransitioning
  const continuePendingLabel = t(isPersisting ? 'Saving' : 'Loading')

  return (
    <>
      <Dialog
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleRequestClose()
        }}
        open={open}
      >
        <DialogContent
          className="legacySelfStudyDialog"
          hideClose
          style={{ translate: 'none' }}
        >
          <LearningLessonShell className="legacySelfStudyShell">
          <LearningExerciseToolbar
            backSlot={
              <LmsButton
                aria-label={backToLearningLabel}
                className="legacySelfStudyBackButton rounded-full"
                leadingIcon={<ArrowLeft aria-hidden="true" className="size-6" />}
                onClick={handleRequestClose}
                size="icon-lg"
                variant="ghost"
              >
                <span className="sr-only">{backToLearningLabel}</span>
              </LmsButton>
            }
            className="legacySelfStudyAppBar"
            centerSlotClassName="legacySelfStudyToolbarCenter"
            leftSlotClassName="legacySelfStudyToolbarLeft"
            progress={progressPercent}
            progressLabel={`${Math.round(progressPercent)}% ${t('saved', { ns: 'book' })}`}
            progressTrackClassName="legacySelfStudyProgress"
            progressValueClassName="legacySelfStudyProgressInner"
            rightSlot={
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <LmsButton aria-label={changeLanguageLabel} className="legacySelfStudyIconButton legacySelfStudyLanguageButton rounded-full" leadingIcon={<Languages aria-hidden="true" className="size-5" />} onClick={handleOpenLanguage} size="icon-lg" variant="ghost">
                      <span className="sr-only">{changeLanguageLabel}</span>
                    </LmsButton>
                  </TooltipTrigger>
                  <TooltipContent>{changeLanguageLabel}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <LmsButton aria-label={openLessonBookLabel} className="legacySelfStudyIconButton legacySelfStudyBookButton rounded-full" leadingIcon={<BookOpen aria-hidden="true" className="size-5" />} onClick={handleOpenOnlineBook} size="icon-lg" variant="ghost">
                      <span className="sr-only">{openLessonBookLabel}</span>
                    </LmsButton>
                  </TooltipTrigger>
                  <TooltipContent>{openLessonBookLabel}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            }
            rightSlotClassName="legacySelfStudyToolbarRight"
            toolbarClassName="legacySelfStudyToolbar"
          />
          <LearningLessonShellContent
            className="legacySelfStudyDialogContent"
            pageWrapClassName="page-wrap legacySelfStudyPageWrap"
            viewportClassName="legacySelfStudyTemplateViewport"
          >
              {isLoading ? (
                <div aria-live="polite" className="legacySelfStudyLoadingState" role="status">
                  <LoaderCircle aria-hidden="true" className="size-8 animate-spin" />
                  <span>{t('Loading')}</span>
                </div>
              ) : null}

              {!isLoading && errorMessage ? (
                <div className="selfStudyStepCard selfStudyError" role="alert">{errorMessage}</div>
              ) : null}

              {!isLoading && !errorMessage && infoMessage ? (
                <div className="selfStudyStepCard selfStudyInfo" role="status">
                  {infoMessage}
                </div>
              ) : null}

              {!isLoading && !errorMessage && chapter && currentSlide ? (
                <SelfStudyStepRenderer
                  chapter={chapter}
                  continuePending={continuePending}
                  continuePendingLabel={continuePendingLabel}
                  elapsedSeconds={elapsedSeconds}
                  key={
                    currentSlide.kind === 'content'
                      ? currentSlide.item.id
                      : currentSlide.kind
                  }
                  onContinue={handleContinue}
                  onNext={() => {
                    void handleNext()
                  }}
                  slide={currentSlide}
                />
              ) : null}
          </LearningLessonShellContent>
          </LearningLessonShell>
        </DialogContent>
      </Dialog>
      <Dialog
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleCloseLanguageDialog()
        }}
        open={isLanguageDialogOpen}
      >
        <DialogContent aria-labelledby="legacy-self-study-language-title" className="legacySelfStudyLanguageDialog" hideClose>
        <DialogTitle id="legacy-self-study-language-title">{t('Display language')}</DialogTitle>
        <div className="legacySelfStudyLanguageDialogBody">
          {languageErrorMessage ? (
            <div className="selfStudyStepCard selfStudyError" role="alert">
              {languageErrorMessage}
            </div>
          ) : null}
          <div className="legacySelfStudyLanguageOptions">
            {state.languages
              .filter((language) => language.enabled)
              .map((language) => {
                const currentLanguage = normalizeDashboardLanguageValue(i18n.language)
                const isSelected = currentLanguage === language.value
                const localeKey = getDashboardLocaleKey(i18n.language)

                return (
                  <LmsButton
                    disabled={isSavingLanguage}
                    fullWidth
                    key={language.value}
                    leadingIcon={
                      <img alt={language.value} className="legacySelfStudyLanguageFlag" src={language.flag} />
                    }
                    onClick={() => {
                      void handleSaveLanguage(language.value)
                    }}
                    variant={isSelected ? 'primary' : 'outline'}
                  >
                    <span className="flex-1 text-left">
                      {language.name[localeKey] ?? language.name.en ?? language.value}
                    </span>
                  </LmsButton>
                )
              })}
          </div>
        </div>
        <DialogFooter>
          <LmsButton
            disabled={isSavingLanguage}
            onClick={handleCloseLanguageDialog}
            variant="outline"
          >
            {t('Close')}
          </LmsButton>
        </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}



